import { createQuery, defineQuery } from "@nova/queryable"
import { AircraftManualStatus } from "@nova/schemas"
import { resolveDroneModelKey } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    fetchMultiQuery,
    useBatchPilotTrackingQuery,
} from "features/pilot-tracking/PilotTrackingQueries"
import useSWR, { mutate } from "swr"
import { useMemo } from "react"

export type AircraftComputedStatus = AircraftManualStatus | "maintenance_due" | "good_to_fly"

type TemplateRow = {
    "template.id": string | null
    "template.condition_usage_flights": number | null
    "template.condition_duration_hours": number | null
    "template.condition_interval_value": number | null
}
type RecordRow = { template_id: string; date_performed: string | Date }
type MissionRow = { started_at: string | Date; airtime_hours: number | null }

export const servicesQuery = defineQuery((aircraftId: string) => ({
    resource: "maintenance_template_aircraft",
    select: [
        "template_id",
        "template.name",
        "template.description",
        "template.instructions",
        "template.condition_usage_flights",
        "template.condition_duration_hours",
        "template.condition_interval_value",
        "template.status_on_trigger",
    ],
    filters: [{ field: "aircraft_id", op: "=", value: aircraftId }],
}))

const droneTemplatesQuery = (aircraftId: string) =>
    createQuery({
        resource: "maintenance_template_aircraft",
        select: [
            "template.id",
            "template.condition_usage_flights",
            "template.condition_duration_hours",
            "template.condition_interval_value",
        ],
        filters: [{ field: "aircraft_id", op: "=", value: aircraftId }],
    })

const globalTemplatesQuery = createQuery({
    resource: "maintenance_templates",
    select: [
        "id",
        "condition_usage_flights",
        "condition_duration_hours",
        "condition_interval_value",
    ],
    filters: [{ field: "apply_to_all", op: "=", value: true }],
})

type ModelTemplateRow = {
    id: unknown
    model_targets: unknown
    condition_usage_flights: number | null
    condition_duration_hours: number | null
    condition_interval_value: number | null
}

const modelTemplatesQuery = createQuery({
    resource: "maintenance_templates",
    select: [
        "id",
        "model_targets",
        "condition_usage_flights",
        "condition_duration_hours",
        "condition_interval_value",
    ],
    filters: [{ field: "apply_to_all", op: "=", value: false }],
})

const modelTemplateToRow = (t: ModelTemplateRow): TemplateRow => ({
    "template.id": t.id as string,
    "template.condition_usage_flights": t.condition_usage_flights,
    "template.condition_duration_hours": t.condition_duration_hours,
    "template.condition_interval_value": t.condition_interval_value,
})

const matchesModelKey = (t: ModelTemplateRow, modelKey: string | null): boolean => {
    if (!modelKey || !Array.isArray(t.model_targets)) return false
    return (t.model_targets as string[]).includes(modelKey)
}

const droneRecordsQuery = (aircraftId: string) =>
    createQuery({
        resource: "maintenance_records",
        select: ["template_id", "date_performed"],
        filters: [{ field: "aircraft_id", op: "=", value: aircraftId }],
    })

const droneMissionsQuery = (aircraftId: string) =>
    createQuery({
        resource: "metadata",
        select: ["started_at", "airtime_hours"],
        filters: [{ field: "mission.aircraft_id", op: "=", value: aircraftId }],
    })

const droneQuery = (aircraftId: string) =>
    createQuery({
        resource: "aircraft",
        select: ["id", "manufacturer", "model"],
        filters: [{ field: "id", op: "=", value: aircraftId }],
    })

const CACHE_KEY = "aircraft-maintenance-status"

export const invalidateAircraftStatusCache = () =>
    mutate((key) => Array.isArray(key) && key[0] === CACHE_KEY)

export const useAircraftMaintenanceStatus = (
    aircraftId: string,
    manualStatus: AircraftManualStatus
): { status: AircraftComputedStatus | null; overdueCount: number; isLoading: boolean } => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const queries = useMemo(
        () => ({
            templates: droneTemplatesQuery(aircraftId),
            globalTemplates: globalTemplatesQuery,
            modelTemplates: modelTemplatesQuery,
            aircraft: droneQuery(aircraftId),
            records: droneRecordsQuery(aircraftId),
            missions: droneMissionsQuery(aircraftId),
        }),
        [aircraftId]
    )

    const { data, isLoading: isDataLoading } = useSWR(
        orgId ? [CACHE_KEY, aircraftId, orgId] : null,
        ([, , oid]) => fetchMultiQuery(oid, queries)
    )

    const { status, overdueCount } = useMemo(() => {
        if (!data?.templates || !data?.records || !data?.missions)
            return { status: null, overdueCount: 0 }
        const drone = data.aircraft?.[0]
        const modelKey = drone
            ? (resolveDroneModelKey(drone.manufacturer, drone.model) ?? null)
            : null
        const includedIds = new Set(data.templates.map((t) => t["template.id"]))
        const globalRows: TemplateRow[] = (data.globalTemplates ?? [])
            .filter((t) => !includedIds.has(t.id as string))
            .map((t) => {
                includedIds.add(t.id as string)
                return {
                    "template.id": t.id as string,
                    "template.condition_usage_flights": t.condition_usage_flights,
                    "template.condition_duration_hours": t.condition_duration_hours,
                    "template.condition_interval_value": t.condition_interval_value,
                }
            })
        const modelRows: TemplateRow[] = ((data.modelTemplates ?? []) as ModelTemplateRow[])
            .filter((t) => !includedIds.has(t.id as string) && matchesModelKey(t, modelKey))
            .map(modelTemplateToRow)
        const allTemplates = [...data.templates, ...globalRows, ...modelRows]
        return {
            status: computeMaintenanceStatus(
                manualStatus,
                allTemplates,
                data.records,
                data.missions
            ),
            overdueCount: computeOverdueCount(allTemplates, data.records, data.missions),
        }
    }, [manualStatus, data])

    return { status, overdueCount, isLoading: isDataLoading }
}

const MS_PER_DAY = 1000 * 60 * 60 * 24

export function computeOverdueCount(
    templates: TemplateRow[],
    records: RecordRow[],
    missions: MissionRow[]
): number {
    let overdueCount = 0
    for (const template of templates) {
        const lastRecord = records
            .filter((r) => r.template_id === template["template.id"])
            .sort(
                (a, b) =>
                    new Date(b.date_performed).getTime() - new Date(a.date_performed).getTime()
            )[0]

        const lastServiceDate = lastRecord ? new Date(lastRecord.date_performed) : new Date(0)
        const missionsAfterLastService = missions.filter(
            (m) => new Date(m.started_at) > lastServiceDate
        )
        const flightsSinceService = missionsAfterLastService.length
        const hoursSinceService = missionsAfterLastService.reduce(
            (sum, m) => sum + (m.airtime_hours || 0),
            0
        )
        const daysSinceService = (Date.now() - lastServiceDate.getTime()) / MS_PER_DAY

        if (
            (template["template.condition_usage_flights"] &&
                flightsSinceService >= template["template.condition_usage_flights"]) ||
            (template["template.condition_duration_hours"] &&
                hoursSinceService >= template["template.condition_duration_hours"]) ||
            (lastRecord &&
                template["template.condition_interval_value"] &&
                daysSinceService >= template["template.condition_interval_value"])
        ) {
            overdueCount++
        }
    }
    return overdueCount
}

const UPCOMING_FLIGHTS_THRESHOLD = 5
const UPCOMING_DAYS_THRESHOLD = 14

export type ConditionState = "current" | "upcoming" | "overdue"

export function computeConditionState(
    manualStatus: AircraftManualStatus,
    templates: TemplateRow[],
    records: RecordRow[],
    missions: MissionRow[]
): ConditionState {
    if (manualStatus === "grounded" || manualStatus === "needs_maintenance") return "overdue"

    let upcoming = false
    for (const template of templates) {
        const lastRecord = records
            .filter((r) => r.template_id === template["template.id"])
            .sort(
                (a, b) =>
                    new Date(b.date_performed).getTime() - new Date(a.date_performed).getTime()
            )[0]

        const lastPerformedDate = lastRecord ? new Date(lastRecord.date_performed) : new Date(0)
        const missionsSince = missions.filter((m) => new Date(m.started_at) > lastPerformedDate)
        const flightsSince = missionsSince.length
        const hoursSince = missionsSince.reduce((sum, m) => sum + (m.airtime_hours || 0), 0)
        const daysSince = (Date.now() - lastPerformedDate.getTime()) / MS_PER_DAY

        const flightLimit = template["template.condition_usage_flights"]
        const hourLimit = template["template.condition_duration_hours"]
        const dayLimit = template["template.condition_interval_value"]

        const pastThreshold =
            (flightLimit && flightsSince >= flightLimit) ||
            (hourLimit && hoursSince >= hourLimit) ||
            (lastRecord && dayLimit && daysSince >= dayLimit)
        if (pastThreshold) return "overdue"

        const approaching =
            (flightLimit && flightLimit - flightsSince <= UPCOMING_FLIGHTS_THRESHOLD) ||
            (lastRecord && dayLimit && dayLimit - daysSince <= UPCOMING_DAYS_THRESHOLD)
        if (approaching) upcoming = true
    }

    return upcoming ? "upcoming" : "current"
}

export function computeMaintenanceStatus(
    manualStatus: AircraftManualStatus,
    templates: TemplateRow[],
    records: RecordRow[],
    missions: MissionRow[]
): AircraftComputedStatus {
    if (manualStatus === "grounded" || manualStatus === "needs_maintenance") return manualStatus

    for (const template of templates) {
        const lastRecord = records
            .filter((r) => r.template_id === template["template.id"])
            .sort(
                (a, b) =>
                    new Date(b.date_performed).getTime() - new Date(a.date_performed).getTime()
            )[0]

        const lastPerformedDate = lastRecord ? new Date(lastRecord.date_performed) : new Date(0)
        const missionsSince = missions.filter((m) => new Date(m.started_at) > lastPerformedDate)
        const flightsSince = missionsSince.length
        const hoursSince = missionsSince.reduce((sum, m) => sum + (m.airtime_hours || 0), 0)
        const daysSince = (Date.now() - lastPerformedDate.getTime()) / MS_PER_DAY

        if (
            template["template.condition_usage_flights"] &&
            flightsSince >= template["template.condition_usage_flights"]
        )
            return "maintenance_due"
        if (
            template["template.condition_duration_hours"] &&
            hoursSince >= template["template.condition_duration_hours"]
        )
            return "maintenance_due"
        if (
            lastRecord &&
            template["template.condition_interval_value"] &&
            daysSince >= template["template.condition_interval_value"]
        )
            return "maintenance_due"
    }

    return "good_to_fly"
}

const allAircraftStatusQueries = {
    templates: createQuery({
        resource: "maintenance_template_aircraft",
        select: [
            "aircraft_id",
            "template.id",
            "template.condition_usage_flights",
            "template.condition_duration_hours",
            "template.condition_interval_value",
        ],
    }),
    globalTemplates: globalTemplatesQuery,
    modelTemplates: modelTemplatesQuery,
    aircraft: createQuery({
        resource: "aircraft",
        select: ["id", "manufacturer", "model"],
    }),
    records: createQuery({
        resource: "maintenance_records",
        select: ["aircraft_id", "template_id", "date_performed"],
    }),
    missions: createQuery({
        resource: "metadata",
        select: ["mission.aircraft_id", "started_at", "airtime_hours"],
    }),
}

export function useAircraftStatuses(
    aircraft: Array<{ id: string; manual_status: string | null }> | null | undefined
): Record<string, AircraftComputedStatus> | null {
    return useAircraftStatusMap(aircraft, computeMaintenanceStatus)
}

export function useAircraftConditionStates(
    aircraft: Array<{ id: string; manual_status: string | null }> | null | undefined
): Record<string, ConditionState> | null {
    return useAircraftStatusMap(aircraft, computeConditionState)
}

function useAircraftStatusMap<T>(
    aircraft: Array<{ id: string; manual_status: string | null }> | null | undefined,
    compute: (
        manualStatus: AircraftManualStatus,
        templates: TemplateRow[],
        records: RecordRow[],
        missions: MissionRow[]
    ) => T
): Record<string, T> | null {
    const { data } = useBatchPilotTrackingQuery(allAircraftStatusQueries)

    return useMemo(() => {
        if (!data || !aircraft) return null
        const modelKeyById = new Map<string, string | null>(
            (data.aircraft ?? []).map((a) => [
                a.id as string,
                resolveDroneModelKey(a.manufacturer, a.model) ?? null,
            ])
        )
        const modelTemplates = (data.modelTemplates ?? []) as ModelTemplateRow[]
        return Object.fromEntries(
            aircraft.map((a) => {
                const specificTemplates = data.templates.filter((t) => t.aircraft_id === a.id)
                const includedIds = new Set(specificTemplates.map((t) => t["template.id"]))
                const globalRows: TemplateRow[] = (data.globalTemplates ?? [])
                    .filter((t) => !includedIds.has(t.id as string))
                    .map((t) => {
                        includedIds.add(t.id as string)
                        return {
                            "template.id": t.id as string,
                            "template.condition_usage_flights": t.condition_usage_flights,
                            "template.condition_duration_hours": t.condition_duration_hours,
                            "template.condition_interval_value": t.condition_interval_value,
                        }
                    })
                const modelKey = modelKeyById.get(a.id) ?? null
                const modelRows: TemplateRow[] = modelTemplates
                    .filter((t) => !includedIds.has(t.id as string) && matchesModelKey(t, modelKey))
                    .map(modelTemplateToRow)
                return [
                    a.id,
                    compute(
                        a.manual_status as AircraftManualStatus,
                        [...specificTemplates, ...globalRows, ...modelRows],
                        data.records.filter((r) => r.aircraft_id === a.id),
                        data.missions.filter((m) => m["mission.aircraft_id"] === a.id)
                    ),
                ]
            })
        )
    }, [data, aircraft, compute])
}
